/**
 * Pure region-selection logic shared by the `/assets` World viewer (lists
 * all six baked regions) and `/fly` (picks the mission's own region + start
 * aerodrome to load) — no fetch, no DOM, unit-testable directly against a
 * manifest's `entries` array.
 */

import type { AssetCategory, BakeManifestEntry, WorldMeta } from "@red-opus/baker/schema";
import { MISSION_REGIONS, type MissionRegion } from "@red-opus/engine";

export interface WorldRegionSummary {
  readonly id: string;
  readonly path: string;
  readonly region: string;
  readonly placementCount: number;
  readonly aerodromeCount: number;
}

const WORLD_CATEGORY: AssetCategory = "world";

/** Every baked `world` entry, summarised and sorted by display region name. */
export function worldRegionEntries(entries: readonly BakeManifestEntry[]): WorldRegionSummary[] {
  return entries
    .filter((entry) => entry.category === WORLD_CATEGORY)
    .map((entry) => {
      const meta = entry.meta as WorldMeta;
      return {
        id: entry.id,
        path: entry.path,
        region: meta.region,
        placementCount: meta.placementCount,
        aerodromeCount: meta.aerodromeCount,
      };
    })
    .sort((a, b) => a.region.localeCompare(b.region));
}

/** Looks up one manifest entry by its `world/<ID>` manifest id. */
export function findWorldEntry(entries: readonly BakeManifestEntry[], id: string): BakeManifestEntry | undefined {
  return entries.find((entry) => entry.category === WORLD_CATEGORY && entry.id === id);
}

/** One `MissionRegion`'s baked world file and the aerodrome `/fly` recentres its scenery on. */
export interface RegionBakeEntry {
  readonly id: string;
  readonly path: string;
  readonly startAerodrome: string;
}

/**
 * Every `MissionRegion`'s baked `.WLD` region and start aerodrome
 * (docs/formats/world-terrain.md's scale-inference addendum for the method).
 * `.WLD` `x`/`y` never resolved to `AERO.DAT` `mapX`/`mapY` at the byte level
 * (world-terrain.md §2.4), so each start aerodrome is picked by the same
 * best-fit heuristic Verdun's originally was: `aerodrome-projection.ts`
 * stretches a region's own aerodromes onto its placement bounding box, and
 * the aerodrome that lands nearest a real baked scenery placement wins —
 * *nearest*, not *the same side*: the heuristic has no way to know which
 * nation actually held a field, so a projection can (and for Verdun, does —
 * St. Mihiel was German-held per `AERODROMES` in
 * `packages/engine/src/campaign/aerodromes.ts`) win regardless of side. That
 * is fine for what this recentre point is: a scenery anchor, not a claim
 * about whose aerodrome the flight starts at operationally.
 *
 * Distances measured once, ad hoc, over the baked JSON during the region lane's
 * work (not a committed `re-sessions/` script) at the VERIFIED 1/8-ft placement
 * scale (`wld-units.ts`):
 *
 * | region  | winner        | nearest placement | runner-up            |
 * |---------|---------------|--------------------|-----------------------|
 * | london  | Hainault Farm | ~118,100 u / 4.5 km | Sutton's Farm (~6.2 km) |
 * | dunkirk | Courtrai      | ~94,400 u / 3.6 km  | St. Marie Cappel (~4.8 km) |
 * | somme   | Cachy         | ~45,800 u / 1.7 km  | Douai (~2.6 km) |
 * | paris   | St. Quentin   | ~16,600 u / 0.6 km  | Villers-Cotterets (~1.8 km) |
 * | verdun  | St. Mihiel    | ~27,600 u / 1.05 km | Sivry (~2.8 km) |
 *
 * (Verdun's row is the pre-existing measurement this table generalises; the
 * other four repeat the identical method. The single "lands near real
 * scenery" regression check for each region lives in `region-catalog.test.ts`,
 * not the full comparison above.)
 */
export const REGION_BAKE: Readonly<Record<MissionRegion, RegionBakeEntry>> = {
  london: { id: "world/LONDON", path: "world/LONDON.json", startAerodrome: "Hainault Farm" },
  dunkirk: { id: "world/DUNKIRK", path: "world/DUNKIRK.json", startAerodrome: "Courtrai" },
  somme: { id: "world/SOMME", path: "world/SOMME.json", startAerodrome: "Cachy" },
  paris: { id: "world/PARIS", path: "world/PARIS.json", startAerodrome: "St. Quentin" },
  verdun: { id: "world/VERDUN", path: "world/VERDUN.json", startAerodrome: "St. Mihiel" },
};

/**
 * `/fly`'s region when no mission names one — the instant dogfight, which has
 * no `MissionSpec` and so no briefed region. Verdun, unchanged from before
 * per-mission regions existed, so every dogfight link and saved tape flown
 * before this lane still shows the same sector.
 */
export const DEFAULT_FLY_REGION: MissionRegion = "verdun";

/** `REGION_BAKE[DEFAULT_FLY_REGION]`'s manifest id — kept as a named export for existing callers/tests. */
export const FLY_REGION_ID = REGION_BAKE[DEFAULT_FLY_REGION].id;
/** The manifest path for `FLY_REGION_ID` (`region-loader.ts` fetches it directly — `/fly` doesn't load the manifest, matching `flight-scene.ts`'s existing hardcoded `PLAYER_MODEL_PATH`). */
export const FLY_REGION_PATH = REGION_BAKE[DEFAULT_FLY_REGION].path;
export const FLY_START_AERODROME = REGION_BAKE[DEFAULT_FLY_REGION].startAerodrome;

/** Every `MissionRegion` has a `REGION_BAKE` entry — asserted once at module load rather than left to a missing-key `undefined` surfacing as a runtime fetch 404 deep in `/fly`. */
function assertAllRegionsBaked(): void {
  for (const region of MISSION_REGIONS) {
    if (!REGION_BAKE[region]) throw new Error(`region-catalog: no REGION_BAKE entry for mission region "${region}"`);
  }
}
assertAllRegionsBaked();

/** The baked world entry a `MissionSpec.region` (or `undefined`, for the instant dogfight) should load. */
export function regionBakeFor(region: MissionRegion | undefined): RegionBakeEntry {
  return REGION_BAKE[region ?? DEFAULT_FLY_REGION];
}
